import type { WidgetMethods, WidgetMountOptions, WidgetSettings } from "@/shared/config";

import { logger } from "@/shared/lib";

import { createWidgetElement } from "../providers/element";
import { resolveApi, resolveSettings } from "./settings";

export interface WidgetInstance extends WidgetMethods {
    readonly settings: Readonly<WidgetSettings>;
    unmount(): void;
}

/** Already mounted widgets, keyed by root_element + mode */
const instances = new Map<string, WidgetInstance>();

function resolveContainer(settings: WidgetSettings): HTMLElement | null {
    // A popup is rendered as a fixed overlay, so it lives right in <body>
    if (settings.mode === "popup") return document.body;

    const container = document.getElementById(settings.root_element);

    if (!container) {
        logger.error(`element #${settings.root_element} is not found`);
    }

    return container;
}

export function mount(options: WidgetMountOptions): WidgetInstance | null {
    const settings = resolveSettings(options?.settings);

    if (!settings) return null;

    const cacheKey = `${settings.root_element}-${settings.mode}`;
    const cached = instances.get(cacheKey);

    if (cached) {
        logger.warn("widget is already mounted, the existing instance is returned");
        return cached;
    }

    const container = resolveContainer(settings);

    if (!container) return null;

    const element = createWidgetElement({ settings, api: resolveApi(options.api) });

    container.append(element);

    // Vue mounts synchronously on append; no exposed methods means the root setup has failed
    if (typeof element.tradeIn !== "function") {
        logger.error("widget failed to mount, see the error above");
        element.remove();
        return null;
    }

    const instance: WidgetInstance = {
        settings,
        tradeIn() {
            if (!element.isConnected) {
                logger.warn("tradeIn() is called on an unmounted widget");
                return;
            }

            element.tradeIn();
        },
        unmount() {
            // Vue unmounts the app inside the custom element when it is disconnected
            element.remove();
            instances.delete(cacheKey);
        },
    };

    instances.set(cacheKey, instance);

    return instance;
}
